import { Box, Chip, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Common/Button';
import { useGetBusinessAddons } from '@/apis/business/addons/hook';
import { BusinessAddon } from '@/apis/business/addons/types';
import {
  ADDON_DISPLAY_ORDER,
  AddonPriceBadgePiece,
  formatCommitmentFull,
  formatDisplayDate,
  getAddonDisplayTitle,
  getAddonIcon,
  getBillingAddonPriceBadgePieces,
  isAddonActive,
  num
} from '@/pages/Business/Setting/Addons/components/addonDisplay';
import { formatDollars } from '@/utils/helperFunctions';
import * as urls from 'routes/config';

export interface AddonChargeLine {
  code: string;
  name?: string;
  component_key?: string;
  type?: string;
  unit_label?: string;
  quantity?: number;
  unit_amount?: number;
  units_used?: number;
  included_units?: number;
  included_units_full?: number;
  prorated?: boolean;
  remaining_days?: number;
  days_in_month?: number;
  estimated_amount?: number;
}

interface ActiveAddOnsUsageProps {
  addonCharges?: AddonChargeLine[];
  totalAddonAmount?: number;
  isLoading?: boolean;
}

function singularize(label?: string) {
  if (!label) return 'unit';
  const trimmed = label.trim();
  if (/ies$/i.test(trimmed)) return trimmed.replace(/ies$/i, 'y');
  if (/s$/i.test(trimmed)) return trimmed.replace(/s$/i, '');
  return trimmed;
}

function displayLineAmount(charge: AddonChargeLine) {
  return num(charge.estimated_amount);
}

function cycleLineAmount(charge: AddonChargeLine) {
  return num(charge.estimated_amount);
}

function formatChargeDescription(charge: AddonChargeLine, addonCode?: string) {
  const qty = num(charge.quantity);
  const unitAmount = charge.unit_amount;
  const unitLabel = charge.unit_label;

  if (addonCode === 'ai_workflow' && charge.type === 'usage_overage') {
    return `Additional Runs (${qty.toLocaleString()} × ${formatDollars(unitAmount)})`;
  }

  if (
    addonCode === 'ai_workflow' &&
    (charge.type === 'recurring_flat' || charge.component_key === 'monthly_price_all_locations')
  ) {
    return 'AI Workflow Charges';
  }

  if (addonCode === 'priority_support' && unitAmount != null && qty > 0) {
    const unit = qty === 1 ? 'location' : 'locations';
    return `Priority Support Charges (${qty.toLocaleString()} ${unit} × ${formatDollars(unitAmount)})`;
  }

  if (
    addonCode === 'concurrency_limit' &&
    unitAmount != null &&
    qty > 0 &&
    (charge.component_key === 'price_per_concurrency' || charge.type === 'recurring_per_unit' || !charge.component_key)
  ) {
    const unit = qty === 1 ? 'call' : 'calls';
    return `Additional Concurrent Calls (${qty.toLocaleString()} ${unit} × ${formatDollars(unitAmount)})`;
  }

  const title = charge.name || charge.code;

  if (unitAmount != null && qty > 0 && unitLabel) {
    const unit = qty === 1 ? singularize(unitLabel) : unitLabel;
    return `${title} Charges (${qty.toLocaleString()} ${unit} × ${formatDollars(unitAmount)})`;
  }

  if (charge.type === 'usage_overage' && qty > 0) {
    return `${title} Overage (${qty} ${unitLabel || 'units'})`;
  }

  return `${title} Charges`;
}

function groupChargesByCode(charges: AddonChargeLine[]) {
  return charges.reduce<Record<string, AddonChargeLine[]>>((acc, charge) => {
    const code = charge.code || 'unknown';
    if (!acc[code]) acc[code] = [];
    acc[code].push(charge);
    return acc;
  }, {});
}

function sortActiveAddons(addons: BusinessAddon[]) {
  return [...addons].sort((a, b) => {
    const ai = ADDON_DISPLAY_ORDER.indexOf(a.code);
    const bi = ADDON_DISPLAY_ORDER.indexOf(b.code);
    const aRank = ai === -1 ? ADDON_DISPLAY_ORDER.length : ai;
    const bRank = bi === -1 ? ADDON_DISPLAY_ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return a.name.localeCompare(b.name);
  });
}

function cycleProrateMeta(addon: BusinessAddon): {
  included: number;
  includedFull: number;
  prorated: boolean;
  remainingDays: number;
  daysInMonth: number;
} | null {
  const includedFull = num(addon.prices?.included_runs_per_month);
  if (!includedFull) return null;

  const activatedRaw = addon.subscription?.activated_at;
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  if (!activatedRaw) {
    return { included: includedFull, includedFull, prorated: false, remainingDays: daysInMonth, daysInMonth };
  }

  const activated = new Date(activatedRaw);
  if (Number.isNaN(activated.getTime())) {
    return { included: includedFull, includedFull, prorated: false, remainingDays: daysInMonth, daysInMonth };
  }

  if (activated.getFullYear() !== now.getFullYear() || activated.getMonth() !== now.getMonth()) {
    return { included: includedFull, includedFull, prorated: false, remainingDays: daysInMonth, daysInMonth };
  }

  const remainingDays = Math.max(daysInMonth - activated.getDate() + 1, 0);
  const included =
    remainingDays >= daysInMonth ? includedFull : Math.round(includedFull * (remainingDays / daysInMonth));

  return {
    included,
    includedFull,
    prorated: included < includedFull,
    remainingDays,
    daysInMonth
  };
}

function AddonPriceBadgeLabel({ pieces }: { pieces: AddonPriceBadgePiece[] }) {
  const theme = useTheme();
  const amountColor = theme.palette.primary.dark;
  const unitColor = theme.palette.primary.main;

  return (
    <Box component='span' sx={{ display: 'inline', fontSize: 12, lineHeight: 1.4, color: unitColor }}>
      {pieces.map((piece, index) => {
        if (piece.type === 'dot') {
          return (
            <Box component='span' key={`dot-${index}`} sx={{ fontWeight: 400 }}>
              {' · '}
            </Box>
          );
        }

        if (piece.type === 'text') {
          return (
            <Box component='span' key={`text-${index}`} sx={{ fontWeight: 400 }}>
              {piece.value}
            </Box>
          );
        }

        const emphasize = piece.emphasizeAmount !== false;

        return (
          <Box component='span' key={`seg-${index}`}>
            <Box
              component='span'
              sx={{
                color: emphasize ? amountColor : unitColor,
                fontWeight: emphasize ? 700 : 400
              }}
            >
              {piece.amount}
            </Box>
            <Box component='span' sx={{ color: unitColor, fontWeight: 400 }}>
              {piece.unit}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function AiWorkflowRunsMeter({
  used,
  included,
  includedFull,
  prorated,
  remainingDays,
  daysInMonth,
  primaryColor
}: {
  used: number;
  included: number;
  includedFull?: number;
  prorated?: boolean;
  remainingDays?: number;
  daysInMonth?: number;
  primaryColor: string;
}) {
  const theme = useTheme();
  const safeIncluded = Math.max(included, 0);
  const remaining = Math.max(safeIncluded - used, 0);
  const over = Math.max(used - safeIncluded, 0);
  const isOver = over > 0;
  // Under limit: fill track by used/included. Over limit: split bar by included vs overage share of used.
  const bluePct = isOver
    ? safeIncluded > 0
      ? (safeIncluded / used) * 100
      : 0
    : safeIncluded > 0
      ? Math.min((used / safeIncluded) * 100, 100)
      : used > 0
        ? 100
        : 0;
  const redPct = isOver && used > 0 ? (over / used) * 100 : 0;
  const showProrateNote =
    Boolean(prorated) &&
    includedFull != null &&
    includedFull > safeIncluded &&
    remainingDays != null &&
    daysInMonth != null;

  return (
    <Box mt={1} mb={0} pb={1.5} borderBottom='1px solid #ECECEC'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={0.75}>
        <Typography fontSize={13} fontWeight={500} color='text.secondary'>
          Runs Used This Cycle
        </Typography>
        <Typography fontSize={13} fontWeight={600}>
          {used.toLocaleString()} / {safeIncluded.toLocaleString()}
        </Typography>
      </Stack>

      <Box display='flex' height={8} borderRadius={99} bgcolor='#E8EEF7' overflow='hidden'>
        {bluePct > 0 && (
          <Box
            width={`${bluePct}%`}
            height='100%'
            bgcolor={primaryColor}
            sx={{
              borderRadius: isOver ? '99px 0 0 99px' : 99,
              minWidth: bluePct > 0 ? 4 : 0
            }}
          />
        )}
        {isOver && (
          <Box
            width={`${redPct}%`}
            height='100%'
            bgcolor={theme.palette.error.main}
            sx={{ borderRadius: '0 99px 99px 0', minWidth: 4 }}
          />
        )}
      </Box>

      <Stack direction='row' justifyContent='space-between' alignItems='center' mt={0.75} gap={1}>
        <Typography fontSize={12} color='text.secondary'>
          {safeIncluded.toLocaleString()} runs included · {remaining.toLocaleString()} remaining
          {showProrateNote
            ? ` (prorated from ${includedFull!.toLocaleString()} for ${remainingDays}/${daysInMonth} days)`
            : ''}
        </Typography>
        {isOver && (
          <Typography fontSize={12} fontWeight={600} color='error.main' whiteSpace='nowrap'>
            {over.toLocaleString()} over
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function ActiveAddOnsUsage({ addonCharges = [], isLoading }: ActiveAddOnsUsageProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: addonsResponse, isLoading: loadingAddons } = useGetBusinessAddons();

  const activeAddons = useMemo(
    () => sortActiveAddons((addonsResponse?.data ?? []).filter(isAddonActive)),
    [addonsResponse?.data]
  );

  const chargesByCode = useMemo(() => groupChargesByCode(addonCharges), [addonCharges]);

  if (isLoading || loadingAddons) {
    return (
      <Paper sx={{ p: 2, border: '1px solid #ececec', borderRadius: 2 }} elevation={0}>
        <ActiveAddOnsSkeleton />
      </Paper>
    );
  }

  if (activeAddons.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1.5}
          mb={2}
        >
          <Box>
            <Typography variant='h6' fontWeight={500}>
              Active Add-Ons
            </Typography>
            <Typography color='text.secondary' fontSize={14} mt={0.25}>
              Add-on pricing and usage for the current billing cycle
            </Typography>
          </Box>
          <Button
            variant='contained'
            onClick={() => navigate(`${urls.business.base}/${urls.business.addons}`)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Manage Add-Ons
          </Button>
        </Stack>

        <Stack spacing={2}>
          {activeAddons.map((addon) => {
            const lines = chargesByCode[addon.code] || [];
            const displayLines = lines.filter((line) => displayLineAmount(line) > 0);
            const usageLine = lines.find((line) => line.type === 'usage_overage');
            const showRunsMeter = addon.code === 'ai_workflow';
            const prorateFallback = showRunsMeter ? cycleProrateMeta(addon) : null;
            const used = num(usageLine?.units_used);
            const included =
              usageLine?.included_units != null
                ? num(usageLine.included_units)
                : (prorateFallback?.included ?? num(addon.prices?.included_runs_per_month));
            const includedFull =
              usageLine?.included_units_full != null
                ? num(usageLine.included_units_full)
                : (prorateFallback?.includedFull ?? num(addon.prices?.included_runs_per_month));
            const isProrated =
              usageLine?.prorated ?? prorateFallback?.prorated ?? (includedFull > 0 && included < includedFull);
            const remainingDays = usageLine?.remaining_days ?? prorateFallback?.remainingDays;
            const daysInMonth = usageLine?.days_in_month ?? prorateFallback?.daysInMonth;
            const addonTotal = lines.reduce((sum, line) => sum + cycleLineAmount(line), 0);
            const pendingCancel = addon.subscription?.status === 'pending_cancellation';
            const pricePieces = getBillingAddonPriceBadgePieces(addon, {
              includedRuns: included,
              isProrated
            });

            return (
              <Paper
                key={addon.id}
                elevation={0}
                sx={{
                  border: '1px solid #ECECEC',
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: '#fff'
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  gap={1}
                  pb={1.5}
                  borderBottom='1px solid #ECECEC'
                >
                  <Stack direction='row' alignItems='center' gap={1} minWidth={0} flexWrap='wrap'>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': { width: 20, height: 20 }
                      }}
                    >
                      {getAddonIcon(addon.code)}
                    </Box>
                    <Typography fontWeight={600} fontSize={15}>
                      {getAddonDisplayTitle(addon)}
                    </Typography>
                    <Chip
                      label={pendingCancel ? `Ends ${formatDisplayDate(addon.subscription?.cancel_at)}` : 'Active'}
                      size='small'
                      sx={{
                        bgcolor: pendingCancel ? theme.palette.warning.light : theme.palette.success.light,
                        color: pendingCancel ? theme.palette.warning.main : theme.palette.success.main,
                        fontWeight: 600,
                        height: 22,
                        fontSize: 12,
                        borderRadius: '100px'
                      }}
                    />
                  </Stack>

                  <Stack direction='row' alignItems='center' gap={1.5} flexWrap='wrap'>
                    <Chip
                      label={<AddonPriceBadgeLabel pieces={pricePieces} />}
                      size='small'
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        borderRadius: '100px',
                        height: 'auto',
                        py: 0.75,
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          px: 1.5,
                          py: 0.25,
                          textAlign: 'left'
                        }
                      }}
                    />
                    <Typography
                      borderLeft='1px solid #ECECEC'
                      pl={1}
                      fontSize={13}
                      color='text.secondary'
                      whiteSpace='nowrap'
                    >
                      {formatCommitmentFull(addon.subscription?.commitment_months ?? addon.commitment_months)}
                    </Typography>
                  </Stack>
                </Stack>

                {showRunsMeter && (
                  <AiWorkflowRunsMeter
                    used={used}
                    included={included}
                    includedFull={includedFull || undefined}
                    prorated={isProrated}
                    remainingDays={remainingDays}
                    daysInMonth={daysInMonth}
                    primaryColor={theme.palette.primary.main}
                  />
                )}

                {displayLines.length > 0 ? (
                  <Stack mb={0} divider={<Box borderBottom='1px solid #ECECEC' />}>
                    {displayLines.map((line, index) => (
                      <Stack
                        key={`${line.component_key || line.type}-${index}`}
                        direction='row'
                        justifyContent='space-between'
                        gap={2}
                        py={1.25}
                      >
                        <Typography fontSize={14} color='text.secondary'>
                          {formatChargeDescription(line, addon.code)}
                        </Typography>
                        <Typography fontSize={14} fontWeight={600} whiteSpace='nowrap'>
                          {formatDollars(displayLineAmount(line))}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  !showRunsMeter && (
                    <Typography fontSize={13} color='text.secondary' mb={1.5}>
                      No recurring charges in this billing cycle yet. Monthly billing starts on the next cycle.
                    </Typography>
                  )
                )}

                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  py={1.25}
                  borderTop='1px solid #ECECEC'
                >
                  <Typography fontWeight={600} fontSize={14}>
                    Add-On Total This Cycle
                  </Typography>
                  <Typography fontWeight={600} fontSize={14}>
                    {formatDollars(addonTotal)}
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

function ActiveAddOnsSkeleton() {
  return (
    <Box>
      <Skeleton variant='text' width={180} height={28} />
      <Skeleton variant='text' width={320} height={18} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {[1, 2].map((key) => (
          <Skeleton key={key} variant='rounded' height={120} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    </Box>
  );
}
